import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setPresencePerson } from 'services/presence';
import {
  monitor,
  flushMonitoring,
  monitoringEnabled,
  readyJoin,
  BOOTSTRAP_RECEIVED,
} from 'services/monitoring';

export function useMonitoringReady(
  sessionId: number,
  ready: boolean,
  engine: 'legacy' | 'fast',
) {
  const [received, setReceived] = useState(0);
  useEffect(() => {
    if (!monitoringEnabled || engine !== 'legacy') return;
    const notify = (event: Event) => {
      if ((event as CustomEvent<number>).detail === sessionId)
        setReceived(value => value + 1);
    };
    window.addEventListener(BOOTSTRAP_RECEIVED, notify);
    return () => window.removeEventListener(BOOTSTRAP_RECEIVED, notify);
  }, [sessionId, engine]);
  useEffect(() => {
    if (monitoringEnabled && ready) readyJoin(sessionId, engine);
  }, [sessionId, ready, engine, received]);
}

export function useMonitoringPerson(
  personId: number | null | undefined,
  sessionId?: number,
  selection?: 'manual' | 'automatic',
) {
  const previous = useRef<number>();
  const mounted = useRef(false);
  useEffect(() => {
    if (sessionId) setPresencePerson(personId || undefined, sessionId);
    if (monitoringEnabled && personId && personId !== previous.current) {
      monitor('person.selected', {
        person_id: personId,
        session_id: sessionId,
        selection: selection || (mounted.current ? 'manual' : 'restored'),
      });
    }
    previous.current = personId || undefined;
    mounted.current = true;
  }, [personId, sessionId, selection]);
}

export default function MonitoringObserver() {
  const { pathname } = useLocation();
  const userId = useSelector((state: any) => state.auth.item.userId);
  const personId = useSelector(
    (state: any) => Number(state.auth.item.selected_person) || undefined,
  );
  const sessionId =
    Number(pathname.match(/\/sessions?\/(\d+)/)?.[1]) || undefined;
  // This cookie is the legacy account selection; native sessions own their own Person.
  useMonitoringPerson(personId);
  useEffect(() => {
    if (userId) monitor('auth.visit');
  }, [userId]);
  useEffect(() => {
    if (!userId || pathname.startsWith('/dashboard/monitoring')) return;
    // Fixed route vocabulary only: no URL query, fragments, names, or arbitrary text.
    const segments = pathname.split('/');
    const page = segments
      .filter(part =>
        /^(dashboard|session|sessions|markets|portfolio|metrics|news|blotter|overview|settings|users|trainers|klasses|reports|data|analysis-studio)$/.test(
          part,
        ),
      )
      .join('/');
    monitor('ui.navigation', {
      session_id: sessionId,
      ...(page ? { page } : {}),
    });
    let lastActivity = 0;
    const activity = (event: Event) => {
      if (
        !event.isTrusted ||
        document.visibilityState !== 'visible' ||
        Date.now() - lastActivity < 30000
      )
        return;
      lastActivity = Date.now();
      monitor('ui.activity', { session_id: sessionId });
    };
    const hidden = () => {
      if (document.visibilityState === 'hidden') flushMonitoring(true);
    };
    document.addEventListener('pointerdown', activity, { passive: true });
    document.addEventListener('keydown', activity, { passive: true });
    document.addEventListener('visibilitychange', hidden);
    return () => {
      document.removeEventListener('pointerdown', activity);
      document.removeEventListener('keydown', activity);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, [pathname, sessionId, userId]);
  return null;
}
